import { Box } from '@mui/material';
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PageTitle } from '../../../components/PageTitle';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs } from '../../../components/Breadcrumbs';
import { Crumb } from '../../../components/Breadcrumbs/interfaces';
import { enqueueSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { LoadingLayover } from '../../../components/LoadingLayover';
import { ROUTES } from '../../../routes/Routes';
import { DueActivityWarning } from './components/DueActivityWarning';
import { isPlannedActivityOverdue } from './helpers';
import { DetailsTab, TabControl } from '../../../components/tabs';
import { DetailsTabPanel } from './tabs/DetailsTabPanel';
import { WorkLogTabPanel } from './tabs/WorkLogTabPanel';
import { WorkLogTab } from './tabs/WorkLogTab';
import { useGetPlannedActivity } from '../../../reactQuery/Activities/activities';

export const PlannedActivityDetails: React.FC = (): JSX.Element => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const params = useParams();

  const [currentTab, setCurrentTab] = useState<number>(0);

  const plannedActivityId = Number(params.id);
  const crumbs: Crumb[] = [
    {
      title: t('breadcrumbs.overview'),
      link: ROUTES.activities.overview,
    },
  ];

  const {
    data: plannedActivity,
    isLoading,
    error,
  } = useGetPlannedActivity(plannedActivityId);

  const handleActityLogNotFound = (): void => {
    enqueueSnackbar(
      t('errors.resourceNotFound', {
        resource: 'contact',
      }),
      {
        variant: 'error',
      }
    );
    navigate('/activities');
  };

  useEffect(() => {
    if (Number.isNaN(plannedActivityId)) {
      handleActityLogNotFound();
    } else if (error) {
      const axiosError = error as AxiosError;

      if (axiosError.response?.status === 404) {
        handleActityLogNotFound();
      } else {
        enqueueSnackbar(t('errors.somethingWentWrong'), {
          variant: 'error',
        });
      }
    }
  }, [plannedActivityId, error, navigate]);

  if (isLoading) {
    return <LoadingLayover />;
  }

  if (!plannedActivity) {
    navigate(ROUTES.activities.overview);
    return <Box>Error</Box>;
  }

  const isOverdue = isPlannedActivityOverdue(plannedActivity);

  return (
    <Fragment>
      <PageTitle title={t('plannedActivities:title')} space={'double'}>
        <Breadcrumbs crumbs={crumbs} current={plannedActivity.title} />
      </PageTitle>

      {isOverdue && <DueActivityWarning plannedActivity={plannedActivity} />}

      <TabControl value={currentTab} onChange={(value) => setCurrentTab(value)}>
        <DetailsTab />
        <WorkLogTab />
      </TabControl>

      <DetailsTabPanel
        value={currentTab}
        index={0}
        plannedActivity={plannedActivity}
      />
      <WorkLogTabPanel
        value={currentTab}
        index={1}
        plannedActivityId={plannedActivity.id}
      />
    </Fragment>
  );
};
